import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, CircularProgress, IconButton, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';

import FileDropZone from '../../common/components/FileDropZone';
import { useAppDispatch } from 'src/redux-app-hooks';
import {
    checkOfferSubmissionPdfConversionJob,
    deleteOfferSubmission,
    fetchOfferSubmissions,
    importOfferSubmission,
    triggerOfferSubmissionPdfConversionJob,
} from './offer-evaluation.slice';
import StepperActions from './components/StepperActions';
import { NotificationSeverity, showSnackbar } from '../notifications/notifications.slice';
import { useAppSelector } from 'src/redux-app-hooks';
import { OfferSubmissionListItem } from './dto/offer-submission.dto';

const PDF_CONVERSION_POLLING_INTERVAL = 3000;
const PDF_CONVERSION_POLLING_TIMEOUT = 20 * 60 * 1000; // 20 minutes

export default function OfferEvaluationStepImportOfferSubmissions() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { tender, offerSubmissions, offerSubmissionsPdfConversionJobs } = useAppSelector(
        (state) => state.offerEvaluation,
    );

    // Tracks cleanup functions for all in-flight polling loops, keyed by fileId.
    const activePollers = useRef<Map<string, () => void>>(new Map());

    // Starts a per-job polling loop for a single PDF conversion job.
    // Polls until the job is done or the component unmounts.
    // Independent loops mean multiple concurrent uploads never interfere.
    const startPollingJob = useCallback(
        (fileId: string) => {
            let cancelled = false;
            const startTime = Date.now();

            const poll = async () => {
                if (cancelled) return;

                // Cancel polling if it takes very long for inactive users
                if (Date.now() - startTime >= PDF_CONVERSION_POLLING_TIMEOUT) {
                    cancel();
                    return;
                }

                const result = await dispatch(checkOfferSubmissionPdfConversionJob(fileId));
                if (cancelled) return;

                const isDone = checkOfferSubmissionPdfConversionJob.fulfilled.match(result) && result.payload.done;
                if (!isDone) {
                    setTimeout(poll, PDF_CONVERSION_POLLING_INTERVAL);
                } else {
                    activePollers.current.delete(fileId);
                }
            };

            const timeoutId = setTimeout(poll, PDF_CONVERSION_POLLING_INTERVAL);

            const cancel = () => {
                cancelled = true;
                clearTimeout(timeoutId);
                activePollers.current.delete(fileId);
            };
            activePollers.current.set(fileId, cancel);
        },
        [dispatch],
    );
    // Cancel all active polling loops on unmount.
    useEffect(() => {
        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            activePollers.current.forEach((cancel) => cancel());
        };
    }, []);

    const handleManualAdd = () => {
        dispatch(importOfferSubmission({ file: null, mimeType: '' }));
    };

    const handleFilesAdd = async (files: File[]) => {
        for (const file of files) {
            if (file.type === 'application/pdf') {
                const result = await dispatch(triggerOfferSubmissionPdfConversionJob(file));
                if (triggerOfferSubmissionPdfConversionJob.fulfilled.match(result)) {
                    startPollingJob(result.payload.fileId);
                }
            } else {
                await dispatch(importOfferSubmission({ file, mimeType: 'application/vnd.gaeb+xml' }));
            }
        }
    };
    const handleDelete = async (offerSubmission: OfferSubmissionListItem) => {
        await dispatch(deleteOfferSubmission(offerSubmission.id));
        if (tender) {
            await dispatch(fetchOfferSubmissions(tender.id));
        }
    };

    const handleNextClick = () => {
        if (offerSubmissions.length === 0) {
            dispatch(
                showSnackbar({
                    message: t('At least one offer submission must be imported.'),
                    severity: NotificationSeverity.Error,
                }),
            );
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (!tender) {
            return;
        }
        dispatch(fetchOfferSubmissions(tender.id));
    }, [dispatch, tender]);

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {t('Import Offer Submissions')}
            </Typography>

            {offerSubmissions.map((offerSubmission) => (
                <Box
                    key={offerSubmission.id}
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        p: 2,
                        mb: 1,
                    }}
                >
                    <Typography sx={{ flexGrow: 1 }}>{offerSubmission.bidderName}</Typography>
                    <IconButton size="small" color="error" onClick={() => handleDelete(offerSubmission)}>
                        <Delete />
                    </IconButton>
                </Box>
            ))}

            <Box>
                {offerSubmissionsPdfConversionJobs.map((job) => (
                    <Box
                        key={job.fileId}
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            border: '1px solid #ccc',
                            borderRadius: 1,
                            p: 2,
                            mb: 1,
                        }}
                    >
                        <CircularProgress size={24} />
                        <Typography sx={{ flexGrow: 1 }}>{job.fileName}</Typography>
                        <Chip label={job.status} color="info" />
                    </Box>
                ))}
            </Box>

            <Box sx={{ mt: 3 }}>
                <FileDropZone onFilesAdd={handleFilesAdd} acceptedExtensions={['.x84', '.pdf']} maxFiles={100} />

                <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Add />}
                    sx={{ mt: 2 }}
                    onClick={handleManualAdd}
                >
                    {t('Enter offer submission manually')}
                </Button>
            </Box>

            <StepperActions onNextClick={handleNextClick} disabledPrevious={true} />
        </>
    );
}
